import { z } from "zod";
import { trimmed, uuid } from "./common";

/**
 * Цели и достижения (`core.staff`): цели заведения, филиала и сотрудника с прогрессом за период (день / неделя / месяц —
 * повторяются сами, или свои даты), рейтинг сотрудников и достижения за действия (заказы, смены, касса без расхождений…).
 * Очки — за достижения (по уровню) и за выполненные личные цели (`points` цели); уровень — по сумме очков.
 */
export const GOAL_METRICS = ["revenue", "orders", "avg_check", "items", "new_customers"] as const;
export type GoalMetric = (typeof GOAL_METRICS)[number];
/** Метрики-деньги (minor units) — остальные в штуках */
export const MONEY_METRICS: readonly GoalMetric[] = ["revenue", "avg_check"];
/** Метрики-средние: цель засчитывается по итогу закрытого периода, а не в момент касания */
export const AVERAGE_METRICS: readonly GoalMetric[] = ["avg_check"];

export const GOAL_PERIODS = ["day", "week", "month", "custom"] as const;
export type GoalPeriod = (typeof GOAL_PERIODS)[number];

export const GOAL_SCOPES = ["company", "branch", "employee"] as const;
export type GoalScope = (typeof GOAL_SCOPES)[number];

const isoDate = z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" });

const goalBase = z.strictObject({
  title: trimmed(1, 120),
  metric: z.enum(GOAL_METRICS),
  period: z.enum(GOAL_PERIODS),
  /** Только для period = custom */
  dateFrom: isoDate.nullish(),
  dateTo: isoDate.nullish(),
  /** Филиал (null — все филиалы компании) */
  branchId: uuid.nullish(),
  /** Сотрудник — личная цель (null — общая цель филиала/компании) */
  userId: uuid.nullish(),
  /** Цель: деньги — minor units, иначе штуки */
  target: z.number().int({ error: "Цель — целое число" }).min(1, { error: "Цель должна быть больше нуля" }).max(1e13),
  /** Для items: только эта позиция меню или категория */
  menuItemId: uuid.nullish(),
  categoryId: uuid.nullish(),
  /** Очки сотруднику за выполнение личной цели */
  points: z.number().int().min(0).max(1000).default(0),
  /** Награда за выполнение — текстом («премия 1000 сом») */
  reward: z.string().trim().max(200).nullish(),
  isActive: z.boolean().default(true),
});

const refineGoal = <T extends z.ZodType<Partial<z.output<typeof goalBase>>>>(s: T) =>
  s
    .refine((g) => g.period !== "custom" || (!!g.dateFrom && !!g.dateTo), { error: "Укажите даты цели", path: ["dateTo"] })
    .refine((g) => !g.dateFrom || !g.dateTo || g.dateTo >= g.dateFrom, { error: "Конец периода раньше начала", path: ["dateTo"] })
    .refine((g) => !g.dateFrom || !g.dateTo || Date.parse(g.dateTo) - Date.parse(g.dateFrom) <= 366 * 86_400_000, { error: "Период не больше года", path: ["dateTo"] })
    .refine((g) => g.metric === undefined || g.metric === "items" || (!g.menuItemId && !g.categoryId), { error: "Позиция или категория — только для цели по количеству блюд", path: ["menuItemId"] })
    .refine((g) => !(g.menuItemId && g.categoryId), { error: "Выберите позицию или категорию", path: ["categoryId"] });

export const goalCreateSchema = refineGoal(goalBase);
export const goalUpdateSchema = refineGoal(goalBase.partial().extend({ version: z.number().int().min(1) }));
export type GoalInput = z.output<typeof goalCreateSchema>;

const flag = z.enum(["true", "false"]).transform((v) => v === "true");
export const goalsQuery = z.object({
  userId: uuid.optional(),
  /** Показать и выключенные */
  all: flag.optional(),
});
export const goalHistoryQuery = z.object({ limit: z.coerce.number().int().min(1).max(100).default(20) });

export const LEADERBOARD_METRICS = ["revenue", "orders", "avg_check", "items", "points"] as const;
export type LeaderboardMetric = (typeof LEADERBOARD_METRICS)[number];
export const leaderboardQuery = z.object({
  metric: z.enum(LEADERBOARD_METRICS).default("revenue"),
  period: z.enum(["day", "week", "month"]).default("week"),
});

/** Достижения: метрика сотрудника за всё время и порог. Новое достижение — сюда + подписи `goals.ach.<код>` в ERP. */
export const ACHIEVEMENT_TIERS = ["bronze", "silver", "gold", "platinum"] as const;
export type AchievementTier = (typeof ACHIEVEMENT_TIERS)[number];
export const TIER_POINTS: Record<AchievementTier, number> = { bronze: 10, silver: 25, gold: 50, platinum: 100 };

export const ACHIEVEMENT_METRICS = ["orders", "items", "big_tables", "shifts", "clean_cash", "customers", "deliveries", "goals", "top_seller"] as const;
export type AchievementMetric = (typeof ACHIEVEMENT_METRICS)[number];

type Achievement = { metric: AchievementMetric; threshold: number; tier: AchievementTier };
export const ACHIEVEMENTS = {
  /** Первый оплаченный заказ сотрудника (официант или кто создал заказ) */
  first_order: { metric: "orders", threshold: 1, tier: "bronze" },
  orders_100: { metric: "orders", threshold: 100, tier: "silver" },
  orders_1000: { metric: "orders", threshold: 1000, tier: "gold" },
  orders_5000: { metric: "orders", threshold: 5000, tier: "platinum" },
  /** Проданных позиций (штук) */
  items_500: { metric: "items", threshold: 500, tier: "silver" },
  items_5000: { metric: "items", threshold: 5000, tier: "gold" },
  /** Заказ на 10 гостей и больше */
  big_table: { metric: "big_tables", threshold: 1, tier: "bronze" },
  /** Закрытые рабочие смены */
  first_shift: { metric: "shifts", threshold: 1, tier: "bronze" },
  shifts_50: { metric: "shifts", threshold: 50, tier: "silver" },
  shifts_200: { metric: "shifts", threshold: 200, tier: "gold" },
  /** Кассовые смены, закрытые без расхождения наличных */
  clean_cash_10: { metric: "clean_cash", threshold: 10, tier: "silver" },
  clean_cash_50: { metric: "clean_cash", threshold: 50, tier: "gold" },
  /** Новые клиенты в базе */
  customers_10: { metric: "customers", threshold: 10, tier: "bronze" },
  customers_100: { metric: "customers", threshold: 100, tier: "silver" },
  /** Доставленные заказы (курьер) */
  deliveries_10: { metric: "deliveries", threshold: 10, tier: "bronze" },
  deliveries_100: { metric: "deliveries", threshold: 100, tier: "silver" },
  deliveries_500: { metric: "deliveries", threshold: 500, tier: "gold" },
  /** Выполненные личные цели */
  goal_first: { metric: "goals", threshold: 1, tier: "bronze" },
  goals_10: { metric: "goals", threshold: 10, tier: "silver" },
  goals_50: { metric: "goals", threshold: 50, tier: "gold" },
  /** Лучший по выручке в филиале за прошлую неделю (продавали хотя бы двое) */
  top_seller: { metric: "top_seller", threshold: 1, tier: "gold" },
} as const satisfies Record<string, Achievement>;
export type AchievementCode = keyof typeof ACHIEVEMENTS;
export const ACHIEVEMENT_CODES = Object.keys(ACHIEVEMENTS) as AchievementCode[];

/** Пороги уровней по очкам: уровень N — от LEVELS[N-1] очков. */
export const LEVELS = [0, 30, 100, 250, 500, 900, 1500] as const;
export function levelOf(points: number): { level: number; from: number; next: number | null } {
  let i = 0;
  while (i + 1 < LEVELS.length && points >= LEVELS[i + 1]!) i++;
  return { level: i + 1, from: LEVELS[i]!, next: LEVELS[i + 1] ?? null };
}

const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Границы периода цели, в который попадает дата `today` (бизнес-дата): неделя — с понедельника, месяц — календарный. */
export function goalPeriodRange(period: GoalPeriod, today: string, custom?: { from?: string | null; to?: string | null }): { from: string; to: string } {
  if (period === "custom") return { from: custom?.from ?? today, to: custom?.to ?? today };
  if (period === "day") return { from: today, to: today };
  if (period === "week") {
    const dow = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
    const from = addDays(today, -dow);
    return { from, to: addDays(from, 6) };
  }
  const from = `${today.slice(0, 7)}-01`;
  const [y, m] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  return { from, to: new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10) };
}

/** Предыдущий период повторяющейся цели (для засчитывания по итогу: средний чек, вчерашний день) */
export function previousGoalPeriod(period: Exclude<GoalPeriod, "custom">, today: string) {
  return goalPeriodRange(period, addDays(goalPeriodRange(period, today).from, -1));
}

/** Доля выполнения 0…100+ (целое). */
export const goalPercent = (value: number, target: number) => (target > 0 ? Math.floor((value / target) * 100) : 0);
